import { useMemo, useState } from 'react';
import { useGetQrCode } from '../api/useGetQrCode';
import { useUpdateQrCode } from '../api/useUpdateQrCode';
import {
  DEFAULT_CUSTOMIZATION,
  type QrCodeRecord,
  type QrCustomization,
} from '../api/types';
import { QrPreview, downloadQr } from './QrPreview';
import { QrCodeEdit } from './QrCodeEdit';

interface Props {
  initial: QrCodeRecord;
  onBack: () => void;
}

const QR_BASE_URL =
  process.env.REACT_APP_QR_BASE_URL?.replace(/\/$/, '') ||
  'https://api.socialsparkapp.net/prod/qr';

export function QrCodeView({ initial, onBack }: Props) {
  const { data, loading, error, refetch } = useGetQrCode(initial.campaign_id);
  const { update, loading: archiveLoading } = useUpdateQrCode();

  // Locally cache the record so optimistic updates (archive/unarchive)
  // and successful edits show immediately without waiting on a refetch.
  const [localRecord, setLocalRecord] = useState<QrCodeRecord | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const record: QrCodeRecord = localRecord ?? data ?? initial;
  const isCampaign = record.type === 'campaign';
  const customization: QrCustomization = record.customization || DEFAULT_CUSTOMIZATION;
  const qrUrl = `${QR_BASE_URL}/${record.campaign_id}`;

  const status = useMemo(() => computeStatus(record), [record]);
  const daysRemaining = useMemo(() => computeDaysRemaining(record), [record]);

  const handleArchiveToggle = async () => {
    setActionError(null);
    setConflict(false);
    const next = !record.archived;
    const result = await update(record.campaign_id, {
      version: record.version || 1,
      archived: next,
    });
    if (result.error) {
      if (result.error.code === 'version_conflict') {
        setConflict(true);
        refetch();
      } else {
        setActionError(result.error.message);
      }
      return;
    }
    if (result.data) setLocalRecord(result.data);
  };

  const handleSaved = (saved: QrCodeRecord) => {
    setLocalRecord(saved);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <QrCodeEdit
        record={record}
        onCancel={() => setIsEditing(false)}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-xs text-gray-400 hover:text-gray-200 mb-2 inline-flex items-center gap-1"
          >
            ← Back to list
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold text-white">
              {record.campaign_name || record.campaign_id}
            </h2>
            <StatusBadge status={status} />
            {!isCampaign && (
              <span className="text-xs text-gray-400">· Permanent</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1 break-all">
            <span className="font-mono">{qrUrl}</span>
            {record.created_at && (
              <span className="ml-2">· Created {formatDate(record.created_at)}</span>
            )}
            {record.created_by && (
              <span className="ml-2">by {record.created_by}</span>
            )}
          </div>
          {record.description && (
            <div className="text-xs text-gray-400 mt-2">{record.description}</div>
          )}
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {actionError && (
        <div className="rounded-md border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-300 flex items-center justify-between">
          <span>{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="text-red-300/80 hover:text-red-200"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {conflict && (
        <div className="rounded-md border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          This QR code was changed by someone else — reloaded to the latest version.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className={`grid gap-3 ${isCampaign ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <Stat label="Total scans" value={(record.scan_count ?? 0).toLocaleString()} />
            <Stat
              label="Last scan"
              value={record.last_scanned_at ? formatRelative(record.last_scanned_at) : '—'}
            />
            {isCampaign && (
              <Stat
                label="Days remaining"
                value={daysRemaining === null ? '—' : `${daysRemaining}`}
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-violet-600 text-white font-medium hover:bg-violet-500"
            >
              Edit style
            </button>
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, record.logo_url, record.campaign_id, 'png')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download PNG
            </button>
            <button
              type="button"
              onClick={() => downloadQr(qrUrl, customization, record.logo_url, record.campaign_id, 'svg')}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-800 text-gray-200 hover:bg-gray-800/50"
            >
              Download SVG
            </button>
            <button
              type="button"
              onClick={handleArchiveToggle}
              disabled={archiveLoading}
              className={[
                'px-3 py-1.5 text-sm rounded-md border transition-colors disabled:opacity-50',
                record.archived
                  ? 'border-emerald-900/50 text-emerald-300 hover:bg-emerald-950/30'
                  : 'border-red-900/50 text-red-400 hover:bg-red-950/30',
              ].join(' ')}
            >
              {archiveLoading ? '…' : record.archived ? 'Unarchive' : 'Archive'}
            </button>
          </div>

          <div className="text-[11px] text-gray-500 leading-relaxed">
            Editing the style preserves the URL, so already-printed QRs keep working.
            {loading && <span className="ml-2 text-gray-600">· Refreshing…</span>}
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 lg:sticky lg:top-6 self-start">
          <div className="bg-white rounded-xl p-4 shadow-lg">
            <QrPreview
              data={qrUrl}
              customization={customization}
              logoUrl={record.logo_url}
              size={240}
            />
          </div>
          <div className="text-[11px] text-gray-500">Rendered from saved style.</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-gray-900/60 border border-gray-800 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-100 mt-0.5">{value}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'active' | 'ended' | 'archived' }) {
  const styles = {
    active: 'bg-emerald-950/50 border-emerald-800/50 text-emerald-300',
    ended: 'bg-gray-800/60 border-gray-700/60 text-gray-300',
    archived: 'bg-amber-950/40 border-amber-900/50 text-amber-300',
  }[status];
  const label = status === 'active' ? '· Active' : status === 'ended' ? '· Ended' : '· Archived';
  return (
    <span className={`px-2 py-0.5 text-[11px] rounded-md border font-medium ${styles}`}>
      {label}
    </span>
  );
}

function computeStatus(r: QrCodeRecord): 'active' | 'ended' | 'archived' {
  if (r.archived) return 'archived';
  if (r.type === 'campaign' && r.end_date) {
    const end = new Date(`${r.end_date}T23:59:59Z`);
    if (!Number.isNaN(end.getTime()) && end.getTime() < Date.now()) return 'ended';
  }
  return 'active';
}

function computeDaysRemaining(r: QrCodeRecord): number | null {
  if (r.type !== 'campaign' || !r.end_date) return null;
  const end = new Date(`${r.end_date}T23:59:59Z`);
  if (Number.isNaN(end.getTime())) return null;
  const diff = Math.ceil((end.getTime() - Date.now()) / 86400000);
  return Math.max(0, diff);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
